import { defineSlotRecipe } from "@pandacss/dev";

export const aromaAvatar = defineSlotRecipe({
  slots: [
    "container",
    "image",
    "badge",
    "title",
    "subtitle",
    "wrap",
    "textWrap",
    "abbreviation",
  ],
  className: "aroma-avatar",
  jsx: ["Avatar"],
  base: {
    container: {
      position: "relative",
      boxSizing: "border-box",
      width: "m",
      minWidth: "m",
      height: "m",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "content.neutral.base.subtlest",
      backgroundColor: "background.decorative.neutral.base-subtle",
      borderRadius: "pill",
      transition: "all 0.3s ease-in-out",
      _aromaticonChildren: {
        fontSize: "{sizes.xs}",
      },
      cursor: "pointer",
      _focusVisible: {
        borderWidth: "m",
        borderStyle: "solid",
        borderColor: "border.brand-bold",
        shadow: "effect.focus-ring.brand",
      },
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      borderRadius: "pill",
      transition: "filter 0.3s ease-in-out",
      _hover: {
        filter: "brightness(80%)",
      },
    },
    badge: {
      position: "absolute",
      bottom: 0,
      right: 0,
      zIndex: 1,
      maxWidth: "100%",
      //temp styles - should be moved to Badge component
      width: "fit-content",
      whiteSpace: "nowrap",
      lineHeight: "125",
      textOverflow: "ellipsis",
      overflow: "hidden",
    },
    title: {
      textStyle: "title.xs",
    },
    subtitle: {
      textStyle: "paragraph.s.regular",
      color: "content.neutral.base.subtle",
    },
    wrap: {
      gap: "gap.2",
    },
    textWrap: {
      flexDirection: "column",
      justifyContent: "center",
    },
    abbreviation: {
      textStyle: "title.s",
    },
  },

  variants: {
    hierarchy: {
      subtle: {},
      bolder: {},
    },
    colorTheme: {
      neutral: {},
      red: {},
      orange: {},
      yellow: {},
      green: {},
      blue: {},
      purple: {},
      pink: {},
      disabled: {
        container: {
          color: "content.neutral.base.disable",
          backgroundColor: "background.neutral.disable.subtle",
          cursor: "default",
        },
        image: {
          cursor: "default",
          filter: "opacity(0.8) saturate(0.8)",
          _hover: {
            filter: "opacity(0.8) saturate(0.8)",
          },
        },
      },
    },
    size: {
      small: {
        container: {
          width: "s",
          minWidth: "s",
          height: "s",
          _aromaticonChildren: {
            fontSize: "{sizes.2xs}",
          },
        },
        subtitle: {
          display: "none",
        },
        abbreviation: {
          textStyle: "title.xs",
        },
      },
      medium: {},
      xl: {
        container: { width: "xl", minWidth: "xl", height: "xl" },
        badge: {
          bottom: "2px",
          right: "2px",
        },
        title: {
          textStyle: "title.s",
          textTransform: "none",
        },
        subtitle: {
          textStyle: "paragraph.s.regular",
        },
        abbreviation: {
          textStyle: "title.m",
        },
      },
      "2xl": {
        container: {
          width: "2xl",
          minWidth: "2xl",
          height: "2xl",
          _aromaticonChildren: {
            fontSize: "{sizes.s}",
          },
        },
        badge: {
          bottom: "3px",
          right: "3px",
        },
        title: {
          textStyle: "title.s",
          textTransform: "none",
        },
        subtitle: {
          textStyle: "paragraph.s.regular",
        },
        abbreviation: {
          textStyle: "title.l",
        },
      },
      jumbo: {
        container: {
          width: "3xl",
          minWidth: "3xl",
          height: "3xl",
          _aromaticonChildren: {
            fontSize: "{sizes.m}",
          },
        },
        badge: {
          bottom: "0",
          left: "50%",
          transform: "translateX(-50%)",
        },
        title: {
          display: "none",
        },
        subtitle: {
          display: "none",
        },
        abbreviation: {
          textStyle: "title.xl",
        },
      },
    },
    withBadge: {
      false: {
        badge: {
          display: "none",
        },
      },
      true: {
        badge: {
          display: "inline-block",
        },
      },
    },
  },
  compoundVariants: [
    {
      hierarchy: "subtle",
      colorTheme: "neutral",
      css: {
        container: {
          backgroundColor: "background.decorative.neutral.base-subtle",
          color: "content.neutral.base.subtlest",
          _hover: {
            backgroundColor: "background.decorative.neutral.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "red",
      css: {
        container: {
          backgroundColor: "background.decorative.red.base-subtle",
          color: "content.decorative.red",
          _hover: {
            backgroundColor: "background.decorative.red.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "orange",
      css: {
        container: {
          backgroundColor: "background.decorative.orange.base-subtle",
          color: "content.decorative.orange",
          _hover: {
            backgroundColor: "background.decorative.orange.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "yellow",
      css: {
        container: {
          backgroundColor: "background.decorative.yellow.base-subtle",
          color: "content.decorative.yellow",
          _hover: {
            backgroundColor: "background.decorative.yellow.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "green",
      css: {
        container: {
          backgroundColor: "background.decorative.green.base-subtle",
          color: "content.decorative.green",
          _hover: {
            backgroundColor: "background.decorative.green.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "blue",
      css: {
        container: {
          backgroundColor: "background.decorative.blue.base-subtle",
          color: "content.decorative.blue",
          _hover: {
            backgroundColor: "background.decorative.blue.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "purple",
      css: {
        container: {
          backgroundColor: "background.decorative.purple.base-subtle",
          color: "content.decorative.purple",
          _hover: {
            backgroundColor: "background.decorative.purple.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "pink",
      css: {
        container: {
          backgroundColor: "background.decorative.pink.base-subtle",
          color: "content.decorative.pink",
          _hover: {
            backgroundColor: "background.decorative.pink.hover-subtle",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "neutral",
      css: {
        container: {
          backgroundColor: "background.decorative.neutral.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.neutral.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "red",
      css: {
        container: {
          backgroundColor: "background.decorative.red.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.red.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "orange",
      css: {
        container: {
          backgroundColor: "background.decorative.orange.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.orange.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "yellow",
      css: {
        container: {
          backgroundColor: "background.decorative.yellow.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.yellow.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "green",
      css: {
        container: {
          backgroundColor: "background.decorative.green.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.green.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "blue",
      css: {
        container: {
          backgroundColor: "background.decorative.blue.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.blue.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "purple",
      css: {
        container: {
          backgroundColor: "background.decorative.purple.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.purple.hover-bolder",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "pink",
      css: {
        container: {
          backgroundColor: "background.decorative.pink.base-bolder",
          color: "content.neutral.reverse.bold",
          _hover: {
            backgroundColor: "background.decorative.pink.hover-bolder",
          },
        },
      },
    },
  ],
  defaultVariants: {
    size: "medium",
    colorTheme: "neutral",
    hierarchy: "subtle",
  },
});
